/**
 * Custom checkout — per-field inline validation z specific error messages.
 *
 * Baymard UX rec: "Replace generic 'Invalid input' with precise guidance:
 * 'ZIP code is too short' or 'Missing @ in email'."
 *
 * Mechanism:
 *  - Input ma `data-error-msg-{required|type|pattern|too-short|too-long}="..."`
 *  - Browser fires `invalid` event przy submit gdy field nie przechodzi validation
 *  - Albo blur handler validation (post first interaction).
 *  - JS odczytuje `input.validity` flags i wybiera odpowiedni message
 *  - Renderuje do `[data-error-for="X"]` span (już istnieje w markup).
 *
 * Validation runs:
 *  - On `blur` (po pierwszej interakcji)
 *  - On `input` (live clear gdy user fixuje błąd)
 *  - On submit (validate wszystkie + scroll do first invalid)
 *
 * Zgody i paczkomat rejestrują się w `checkout-validation-registry.js`; submit zbiera
 * je razem z polami, więc podsumowanie pokazuje wszystkie braki naraz.
 */
import { runCheckoutChecks } from './checkout-validation-registry.js';

const ERROR_KEYS = {
    valueMissing:    'errorMsgRequired',
    typeMismatch:    'errorMsgType',
    patternMismatch: 'errorMsgPattern',
    tooShort:        'errorMsgTooShort',
    tooLong:         'errorMsgTooLong',
    rangeUnderflow:  'errorMsgRangeUnderflow',
    rangeOverflow:   'errorMsgRangeOverflow',
};

const FALLBACK_MSG = {
    valueMissing:    'To pole jest wymagane',
    typeMismatch:    'Nieprawidłowy format',
    patternMismatch: 'Nieprawidłowy format',
    tooShort:        'Wartość jest za krótka',
    tooLong:         'Wartość jest za długa',
};

/* Kontrolki ilości w podsumowaniu (data-hn-co-cart-qty) NIE są polami checkoutu —
 * mają własną obsługę (checkout-cart-qty.js) i nie mogą blokować submitu cryptic błędem. */
function isCheckoutField(input) {
    return !input.hasAttribute('data-hn-co-cart-qty') && !input.closest('[data-hn-co-required-consent]');
}

function getMessageFor(input) {
    if (!input || !input.validity || input.validity.valid) return null;
    for (const key of Object.keys(ERROR_KEYS)) {
        if (input.validity[key]) {
            const dataKey = ERROR_KEYS[key];
            return input.dataset[dataKey] || FALLBACK_MSG[key] || 'Nieprawidłowa wartość';
        }
    }
    return null;
}

function getErrorEl(input) {
    if (!input.name) return null;
    return document.querySelector(`[data-error-for="${input.name}"]`);
}

function setDescribedBy(input, id, on) {
    const ids = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter((x) => x && x !== id);
    if (on) ids.push(id);
    if (ids.length) input.setAttribute('aria-describedby', ids.join(' '));
    else input.removeAttribute('aria-describedby');
}

function showError(input, msg) {
    const err = getErrorEl(input);
    if (err) {
        err.id = err.id || `hn-err-${input.name.replace(/[^\w-]/g, '_')}`;
        err.textContent = msg;
        err.hidden = false;
        setDescribedBy(input, err.id, true);
    }
    input.classList.add('is-invalid');
    input.setAttribute('aria-invalid', 'true');
}

function clearError(input) {
    const err = getErrorEl(input);
    if (err) {
        err.textContent = '';
        err.hidden = true;
        if (err.id) setDescribedBy(input, err.id, false);
    }
    input.classList.remove('is-invalid');
    input.removeAttribute('aria-invalid');
}

/**
 * Validation gate: pole jest validowane tylko jeśli user już z nim wszedł w interakcję
 * (blur, submit). Bez tego user widzi error message zanim w ogóle zaczął pisać.
 */
function shouldValidate(input) {
    return input.dataset.hnValidationTouched === '1';
}

function markTouched(input) {
    input.dataset.hnValidationTouched = '1';
}

function validateNow(input) {
    if (!shouldValidate(input)) return true;
    const msg = getMessageFor(input);
    if (msg) {
        showError(input, msg);
        return false;
    }
    clearError(input);
    return true;
}

/**
 * Polish pluralizacja "pole" (1) / "pola" (2-4 nieteen) / "pól" (5+, 12-14).
 */
function plurals(n) {
    if (n === 1) return 'pole';
    const lastTwo = n % 100;
    if (lastTwo >= 12 && lastTwo <= 14) return 'pól';
    const last = n % 10;
    if (last >= 2 && last <= 4) return 'pola';
    return 'pól';
}

/**
 * Wyciąga label tekstowy pola (z `<label for="X">` lub fallback do `name`).
 * Czyści gwiazdkę required + whitespace.
 */
function getFieldLabel(input) {
    if (input.id) {
        const label = document.querySelector(`label[for="${input.id}"]`);
        if (label) {
            return label.textContent.replace(/\*/g, '').replace(/\s+/g, ' ').trim().replace(/:$/, '');
        }
    }
    return input.name || 'Pole';
}

/**
 * Renderuje (lub aktualizuje) error summary banner na top formy.
 * Klikalny `<button>` per error → scroll + focus do pola.
 */
function renderErrorSummary(form, errors) {
    let summary = form.querySelector('[data-hn-co-error-summary]');
    if (!summary) {
        summary = document.createElement('div');
        summary.className = 'hn-co__error-summary';
        summary.setAttribute('role', 'alert');
        summary.setAttribute('aria-live', 'assertive');
        summary.setAttribute('data-hn-co-error-summary', '');
        form.prepend(summary);
    }

    const count = errors.length;

    summary.innerHTML = `
        <div class="hn-co__error-summary-head">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="13"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <strong>Popraw ${count === 1 ? '1 pole' : count + ' ' + plurals(count)}, żeby kontynuować</strong>
        </div>
        <ul class="hn-co__error-summary-list"></ul>
    `;
    const list = summary.querySelector('.hn-co__error-summary-list');
    errors.forEach(({ el, label, msg }) => {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'hn-co__error-summary-link';
        const strong = document.createElement('strong');
        strong.textContent = label;
        btn.append(strong, `: ${msg}`);
        btn.addEventListener('click', () => focusError(el));
        li.appendChild(btn);
        list.appendChild(li);
    });
    summary.hidden = false;
}

function focusError(el) {
    if (!el) return;
    if (!el.matches('a[href], button, input, select, textarea, [tabindex]')) el.setAttribute('tabindex', '-1');
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => el.focus({ preventScroll: true }), 350);
}

function fieldError(input) {
    return { el: input, label: getFieldLabel(input), msg: getMessageFor(input) || 'Sprawdź wartość' };
}

function byDomOrder(a, b) {
    if (!a.el || !b.el || a.el === b.el) return 0;
    return a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
}

function hideErrorSummary(form) {
    const summary = form.querySelector('[data-hn-co-error-summary]');
    if (summary) summary.hidden = true;
}

/**
 * Plugin patch: Paynow BLIK input (pay-by-paynow-pl).
 *
 * Plugin `pay-by-paynow-pl` (templates/blik_payment.php) renderuje input z
 * broken attributes:
 *   <input id="paynow_blik_code"
 *          pattern="[0-9]{3} [0-9]{3}"   ← wymaga "123 456" (7 znaków)
 *          minlength="6" maxlength="6"   ← ale max 6 znaków → spacja się nie zmieści
 *          ...>
 *
 * Konflikt: user wpisuje `123456` (prawidłowy BLIK code) → pattern fail
 * → nasz validator pokazuje "Nieprawidłowy format". User wpisuje `123 456` →
 * maxlength=6 blokuje. Niemożliwe do passnięcia frontend validation.
 *
 * Fix: normalizujemy pattern na `[0-9]{6}` (akceptuje 6 cyfr ciągiem) + strip
 * non-digit on input. Paynow server-side akceptuje 6 cyfr bez problemu.
 *
 * TODO: usunąć gdy plugin Paynow naprawi swój pattern. Issue zgłaszany?
 */
function patchPaynowBlikInput(input) {
    if (!input || input.dataset.hnBlikFixed === '1') return;
    input.dataset.hnBlikFixed = '1';
    input.setAttribute('pattern', '[0-9]{6}');

    /* Custom error messages — używane przez nasz validator przez `data-error-msg-*`
     * (patrz ERROR_KEYS mapping na górze pliku). Bez tego user widziałby generyczne
     * "To pole jest wymagane" / "Nieprawidłowy format". */
    input.setAttribute('data-error-msg-required', 'Wpisz 6-cyfrowy kod BLIK z aplikacji Twojego banku');
    input.setAttribute('data-error-msg-pattern', 'Kod BLIK musi mieć dokładnie 6 cyfr');
    input.setAttribute('data-error-msg-too-short', 'Kod BLIK musi mieć 6 cyfr');

    input.addEventListener('input', () => {
        const digits = input.value.replace(/\D/g, '').slice(0, 6);
        if (input.value !== digits) {
            input.value = digits;
        }
    });
}

/**
 * Toggle BLIK input `required` w oparciu o wybraną metodę płatności.
 *
 * Bez tego HTML5 constraint API uważałby pusty BLIK input za valid (pattern fires
 * tylko na non-empty value), klient z BLIK gateway selected mógł submitnąć
 * pustego BLIK code → WC tworzył order pending → backend Paynow fail → klient
 * retry → duplikaty.
 *
 * Required dodajemy tylko gdy `pay_by_paynow_pl_blik` jest selected — inaczej
 * inny payment gateway (z BLIK input ukrytym w innym `.payment_box`) blokowałby
 * submit "to pole wymagane" mimo że klient go nie widzi.
 */
function syncBlikRequired() {
    const blik = document.querySelector('#paynow_blik_code');
    if (!blik) return;
    const selected = document.querySelector('input[name="payment_method"]:checked');
    const isBlikSelected = !!(selected && selected.value === 'pay_by_paynow_pl_blik');
    if (isBlikSelected) {
        blik.setAttribute('required', 'required');
    } else {
        blik.removeAttribute('required');
        /* Cleanup widocznego błędu jeśli klient zmienił metodę na nie-BLIK. */
        clearError(blik);
    }
}

export function initCheckoutFieldValidate() {
    const form = document.querySelector('form.woocommerce-checkout, form.checkout.hn-co');
    if (!form) return;

    // Standardowy WC checkout: tylko zgody i paczkomat, bez walidacji pól.
    if (!form.classList.contains('hn-co')) {
        form.addEventListener('submit', (e) => {
            if (e.target !== form) return;
            const errors = runCheckoutChecks().sort(byDomOrder);
            if (!errors.length) return;
            e.preventDefault();
            e.stopImmediatePropagation();
            focusError(errors[0].el);
            form.dispatchEvent(new CustomEvent('hn-co-validation-failed', { bubbles: false }));
        }, true);
        return;
    }

    // BLIK input pojawia się dynamicznie po wyborze gateway. Patch teraz + obserwuj.
    const existingBlik = form.querySelector('#paynow_blik_code');
    if (existingBlik) patchPaynowBlikInput(existingBlik);
    new MutationObserver((mutations) => {
        for (const m of mutations) {
            for (const node of m.addedNodes) {
                if (node.nodeType !== 1) continue;
                if (node.id === 'paynow_blik_code') {
                    patchPaynowBlikInput(node);
                } else if (node.querySelector) {
                    const found = node.querySelector('#paynow_blik_code');
                    if (found) patchPaynowBlikInput(found);
                }
            }
        }
        /* Po każdej mutation re-sync `required` — DOM mógł zostać rebuilt
         * przez WC AJAX (update_checkout) lub theme REST refresh; nowy radio
         * payment_method mógł zmienić current selection. */
        syncBlikRequired();
    }).observe(form, { childList: true, subtree: true });

    /* Initial state + przy każdej zmianie wyboru metody płatności. */
    syncBlikRequired();
    document.addEventListener('change', (e) => {
        if (e.target && e.target.matches('input[name="payment_method"]')) {
            syncBlikRequired();
        }
    });

    // Blur — pierwsza interakcja, mark touched + validate.
    form.addEventListener('blur', (e) => {
        const input = e.target;
        if (!input || !input.matches('input, textarea, select')) return;
        if (!input.willValidate || !isCheckoutField(input)) return; // type="hidden", disabled, qty-controls
        markTouched(input);
        validateNow(input);
    }, true);

    // Input — live re-validation gdy user koryguje błędne pole.
    form.addEventListener('input', (e) => {
        const input = e.target;
        if (!input || !input.matches('input, textarea, select')) return;
        if (!isCheckoutField(input)) return;
        if (!shouldValidate(input)) return;
        // Re-validate gdy error visible — jeśli teraz valid, czyść error live.
        validateNow(input);
    });

    // Submit — validate wszystkie + scroll do pierwszego invalid + error summary banner.
    form.addEventListener('submit', (e) => {
        if (e.target !== form) return; // pomiń nested forms (np. coupon w order-review)
        const fields = Array.from(form.querySelectorAll('input, textarea, select'))
            .filter((f) => f.willValidate && isCheckoutField(f));
        const errors = [];
        fields.forEach((input) => {
            markTouched(input);
            if (!validateNow(input)) errors.push(fieldError(input));
        });
        errors.push(...runCheckoutChecks());
        errors.sort(byDomOrder);
        if (errors.length) {
            e.preventDefault();
            e.stopImmediatePropagation();
            renderErrorSummary(form, errors);
            focusError(errors[0].el);
            /* Dispatch custom event — `checkout-submit-lock.js` słucha żeby unlock
             * button (na wypadek gdyby został locked przez submit event bubble race). */
            form.dispatchEvent(new CustomEvent('hn-co-validation-failed', { bubbles: false }));
        } else {
            hideErrorSummary(form);
        }
    }, true); // capture phase — przed WC core jQuery handler

    // Browser `invalid` event — gdy native HTML5 submit triggers constraint check.
    // Capture żeby zatrzymać native tooltip i renderować nasz custom message.
    form.addEventListener('invalid', (e) => {
        const input = e.target;
        if (!input || !input.matches('input, textarea, select')) return;
        e.preventDefault(); // suppress native browser tooltip
        if (!isCheckoutField(input)) return; // qty-controls — nie blokują checkoutu
        markTouched(input);
        validateNow(input);
    }, true);
}
